import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { VerificationSession } from '../../../types/verification';
import type { PreviewItem } from './previewMapping';

interface ConvertPreviewStepProps {
  previewItems: PreviewItem[];
  targetMode: 'existing' | 'new';
  newSessionTitle: string;
  existingSessions: VerificationSession[];
  selectedSessionId: string;
}

const headerCell = 'sticky top-0 z-[1] border-b bg-card px-2 py-2 text-xs leading-[1.5]';
const bodyCell = 'border-b px-2 py-1.5 align-middle';
const chip = 'h-5 rounded-md px-2 text-[0.65rem] leading-[1.5]';

const ConvertPreviewStep: React.FC<ConvertPreviewStepProps> = ({
  previewItems,
  targetMode,
  newSessionTitle,
  existingSessions,
  selectedSessionId,
}) => {
  const { t } = useTranslation();

  const getClassificationLabel = (classification: string) => (
    t(`drbfm.convert.classifications.${classification}`, classification)
  );

  const getMethodLabel = (method: string) => (
    t(`drbfm.convert.methods.${method}`, method)
  );

  return (
    <div>
      <p className="revy-cjk revy-figure mb-4 text-sm leading-[1.5] text-muted-foreground">
        {t('drbfm.convert.previewDescription', { count: previewItems.length })}
      </p>

      {/* The scroll container must be this element, not ui/table's own wrapper,
          or the sticky header has nothing that scrolls to stick to. */}
      <div className="max-h-[350px] overflow-auto rounded-md border bg-card">
        <table className="w-full caption-bottom border-separate border-spacing-0 text-sm">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.convert.itemName')}
              </TableHead>
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.convert.classification')}
              </TableHead>
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.convert.standardTarget')}
              </TableHead>
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.convert.verificationMethod')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {previewItems.map((item, idx) => (
              <TableRow key={idx} className="border-b-0">
                <TableCell className={cn(bodyCell, 'max-w-[200px]')}>
                  <p className="revy-cjk truncate text-sm leading-[1.5]" title={item.itemName}>
                    {item.itemName}
                  </p>
                </TableCell>
                <TableCell className={bodyCell}>
                  <div className="flex flex-wrap gap-1">
                    {item.classifications.map((cls) => (
                      <Badge key={cls} variant="outline" className={cn(chip, 'revy-cjk border')}>
                        {getClassificationLabel(cls)}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className={cn(bodyCell, 'max-w-[200px]')}>
                  <p
                    className="revy-cjk truncate text-sm leading-[1.5]"
                    title={item.standardTarget || '-'}
                  >
                    {item.standardTarget || '-'}
                  </p>
                </TableCell>
                <TableCell className={bodyCell}>
                  <div className="flex flex-wrap gap-1">
                    {item.methods.map((m) => (
                      <Badge
                        key={m}
                        variant="outline"
                        className={cn(chip, 'revy-cjk border border-primary text-primary')}
                      >
                        {getMethodLabel(m)}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>

      <Alert variant="info" className="mt-4">
        <Info strokeWidth={1.9} />
        <AlertDescription className="revy-cjk leading-[1.6]">
          {t('drbfm.convert.destination')}{' '}
          {targetMode === 'new'
            ? t('drbfm.convert.destinationNew', { title: newSessionTitle })
            : t('drbfm.convert.destinationExisting', {
                title: existingSessions.find((s) => s.id === selectedSessionId)?.title || '',
              })}
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ConvertPreviewStep;
